import React from "react";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { Row, Col } from "reactstrap";
import { InputText } from "primereact/inputtext";
import EditorContainer from "../../components/EditorContainer";
import { createTenant, updateTenant, getTenant } from "../../api/tenants";
import Address from "../../components/Address";
import { addressSchema } from "../../utils/customValidators";
import { AvForm, AvField, AvGroup, AvInput, AvFeedback, AvRadioGroup, AvRadio } from 'availity-reactstrap-validation';
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import roles from "../../auth/roles";

const schema = () =>
  Yup.object({
    name: Yup.string()
      .required("Ragione Sociale / Cognome - Nome è un campo obbligatorio")
      .nullable(),
    taxCode: Yup.string().taxCode().nullable(),
    vatCode: Yup.string()
      .vatCode().nullable()
      .when("taxCode", (taxCode, schema) => {
        return taxCode
          ? schema
          : schema.required(
            "Inserire la Partita Iva o il Codice Fiscale"
          );
      }),
    email: Yup.string()
      .required("Email è un campo obbligatorio")
      .email("Inserire una email valida"),
    phone: Yup.string()
      .required("Telefono è un campo obbligatorio")
      .nullable(),
    invCode: Yup.string().nullable(),
    modelId: Yup.string().nullable(),
    marketingId: Yup.string().nullable(),
    reference1: Yup.string().required(
      "Inserire la persona di riferimento"
    ),
    reference2: Yup.string(),
    reference3: Yup.string()
  });

const TenantEditor = ({ readOnly }) => {
  const { id, parent } = useParams();

  const onCreate = async tenant => {
    const result = await createTenant(tenant);
    return result;
  };

  const onLoad = async id => {
    const tenant = await getTenant(id);
    return tenant;
  };

  const onUpdate = async tenant => {
    const result = await updateTenant(tenant);
    return result;
  };

  const { user, activeTenant } = useAuth0();
  const isAdmin = user.isInRole([roles.ADMIN], activeTenant)

  return (
    <EditorContainer
      key={id ? `${id}-tenant` : "new-tenant"}
      canSave={true}
      schema={schema}
      readOnly={readOnly}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onLoad={onLoad}
      inValues={parent ? { parent } : null}
      optionalNav={{
        link: parent ? `/admin/tenants-hierarchy/${parent}` : "/",
        text: parent ? "Struttura" : "Dealer"
      }}
      redirRoot="/"
      title={el =>
        `${el.name ? (el.parent ? "Filiale: " : "Dealer: ") + el.name : (el.parent ? "NUOVA FILIALE" : "NUOVO DEALER")}`
      }
      children={(frmProps, containerHandleChange) => (
        <Row>
          <Col sm="6" xs="12">
            <strong className="mt-2">Ragione Sociale / Cognome - Nome</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.name ? "p-error" : ""}
              value={frmProps.values["name"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("name", e)}
            />
            {frmProps.errors.name ? (<div className="invalid-feedback">{frmProps.errors.name}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Email</strong>
            <InputText
              keyfilter="email"
              disabled={readOnly ? true : false}
              className={frmProps.errors.email ? "p-error" : ""}
              value={frmProps.values["email"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("email", e)}
            />
            {frmProps.errors.email ? (<div className="invalid-feedback">{frmProps.errors.email}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Codice Fiscale</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.taxCode ? "p-error" : ""}
              value={frmProps.values["taxCode"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("taxCode", e)}
            />
            {frmProps.errors.taxCode ? (<div className="invalid-feedback">{frmProps.errors.taxCode}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Partita Iva</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.vatCode ? "p-error" : ""}
              value={frmProps.values["vatCode"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("vatCode", e)}
            />
            {frmProps.errors.vatCode ? (<div className="invalid-feedback">{frmProps.errors.vatCode}</div>) : null}
          </Col>

          <Address
            errors={frmProps.errors.address || {}}
            address={frmProps.values["address"] || {}}
            onChange={e => containerHandleChange("address", e)}
          />
          <Col sm="6" xs="12">
            <strong className="mt-2">Telefono</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.phone ? "p-error" : ""}
              value={frmProps.values["phone"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("phone", e)}
            />
            {frmProps.errors.phone ? (<div className="invalid-feedback">{frmProps.errors.phone}</div>) : null}
          </Col>
          <Col sm="2" xs="4">
            <strong className="mt-2">Cod. Fatturazione Elettronica</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.invCode ? "p-error" : ""}
              value={frmProps.values["invCode"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("invCode", e)}
            />
          </Col>
          {isAdmin && (
            <React.Fragment>
              <Col sm="2" xs="4">
                <strong className="mt-2">Cod. Modelli Tagliandi</strong>
                <InputText
                  disabled={readOnly ? true : false}
                  className={frmProps.errors.modelId ? "p-error" : ""}
                  value={frmProps.values["modelId"] || ""}
                  style={{ width: "100%" }}
                  onChange={e => containerHandleChange("modelId", e)}
                />
              </Col>
              <Col sm="2" xs="4">
                <strong className="mt-2">Cod. Campagne Marketing</strong>
                <InputText
                  disabled={readOnly ? true : false}
                  className={frmProps.errors.marketingId ? "p-error" : ""}
                  value={frmProps.values["marketingId"] || ""}
                  style={{ width: "100%" }}
                  onChange={e => containerHandleChange("marketingId", e)}
                />
              </Col>
            </React.Fragment>
          )}

          <Col sm="6" xs="12">
            <strong className="mt-2 text-primary">Persona di riferimento 1</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.reference1 ? "p-error" : ""}
              value={frmProps.values["reference1"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("reference1", e)}
            />
            {frmProps.errors.reference1 ? (<div className="invalid-feedback">{frmProps.errors.reference1}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2 text-primary">Persona di riferimento 2</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.reference2 ? "p-error" : ""}
              value={frmProps.values["reference2"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("reference2", e)}
            />
          </Col>
          {/* <Col sm="6" xs="12">
            <strong className="mt-2">Persona di riferimento 3</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.reference3 ? "p-error" : ""}
              value={frmProps.values["reference3"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("reference3", e)}
              placeholder="persona rif 3"
            />
          </Col> */}
        </Row>
      )}
    />
  );
};
export default TenantEditor;
